import React, { useState, useMemo } from 'react';
import { CYPHER_TEMPLATES, type CypherTemplate } from './templateData';
import type { GraphSchema, GraphNode } from '../../api/dastarkhan';
import {
  Button,
  Input,
  Select,
  Card,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

interface CypherTemplatesProps {
  onApply: (cypher: string) => void;
  schema?: GraphSchema | null;
  nodes?: GraphNode[];
}

export const CypherTemplates: React.FC<CypherTemplatesProps> = ({ onApply, schema, nodes }) => {
  const { theme } = useTheme();
  const [selectedId, setSelectedId] = useState('');
  const [paramValues, setParamValues] = useState<Record<string, string>>({});

  const template = CYPHER_TEMPLATES.find((t) => t.id === selectedId);

  const nodeNameSuggestions = useMemo(() => {
    if (!nodes) return [];
    return nodes
      .map((n) => (n.properties?.name as string) || (n.properties?.Name as string) || '')
      .filter(Boolean)
      .sort();
  }, [nodes]);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setParamValues({});
  };

  const handleApply = () => {
    if (!template) return;
    let cypher = template.cypher;
    for (const param of template.params) {
      const val = paramValues[param.name] || '';
      if (param.type === 'node_label' || param.name === 'label') {
        cypher = cypher.replace(`$${param.name}`, val);
      } else if (param.type === 'number') {
        cypher = cypher.replace(`$${param.name}`, val || '1');
      } else {
        cypher = cypher.replace(`$${param.name}`, `"${val}"`);
      }
    }
    onApply(cypher);
  };

  const templateOptions = [
    { value: '', label: 'Select a template...' },
    ...CYPHER_TEMPLATES.map((t) => ({ value: t.id, label: t.name })),
  ];

  const renderParamInput = (param: CypherTemplate['params'][0]) => {
    const value = paramValues[param.name] || '';
    const onChange = (val: string) =>
      setParamValues((prev) => ({ ...prev, [param.name]: val }));

    if (param.type === 'node_label' && schema?.node_labels && schema.node_labels.length > 0) {
      return (
        <Select
          key={param.name}
          label={param.label}
          size="sm"
          value={value}
          onChange={(value: string) => onChange(value)}
          options={[
            { value: '', label: param.placeholder || 'Select...' },
            ...schema.node_labels.map((l) => ({ value: l, label: l })),
          ]}
        />
      );
    }

    if (param.type === 'node_name') {
      const dlId = `node-name-${param.name}`;
      return (
        <div key={param.name}>
          <Input
            label={param.label}
            size="sm"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            placeholder={param.placeholder}
            list={dlId}
          />
          <datalist id={dlId}>
            {nodeNameSuggestions.map((name) => <option key={name} value={name} />)}
          </datalist>
        </div>
      );
    }

    if (param.type === 'edge_type' && schema?.relationship_types && schema.relationship_types.length > 0) {
      return (
        <Select
          key={param.name}
          label={param.label}
          size="sm"
          value={value}
          onChange={(value: string) => onChange(value)}
          options={[
            { value: '', label: param.placeholder || 'Select...' },
            ...schema.relationship_types.map((t) => ({ value: t, label: t })),
          ]}
        />
      );
    }

    return (
      <Input
        key={param.name}
        label={param.label}
        size="sm"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        placeholder={param.placeholder}
      />
    );
  };

  return (
    <Card style={{ overflow: 'visible' }}>
      <Card.Body>
        <Stack gap="sm">
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: theme.colors.text.primary }}>
            Query Templates
          </span>
          <Select
            size="sm"
            value={selectedId}
            onChange={(value: string) => handleSelect(value)}
            options={templateOptions}
          />
          {template && (
            <>
              <p style={{ margin: 0, fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                {template.description}
              </p>
              {template.params.map(renderParamInput)}
              <Flex justify="end">
                <Button variant="solid" size="sm" onClick={handleApply}>
                  Apply to Editor
                </Button>
              </Flex>
            </>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );
};
